import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { documentRegistersApi, documentApi, templateApi } from '../services/api';
import { RegisterManagementPage } from './RegisterManagementPage';
import type { Template } from '../types/models';

export const DocumentRegistersPage: React.FC = () => {
  // Fetch templates dynamically from the API
  const { data: templatesData } = useQuery({
    queryKey: ['templates', 'document'],
    queryFn: () => templateApi.list('document'),
  });

  // Nothing ever increments a documentCount field on the register record itself (confirmed -
  // it doesn't exist anywhere in prod-nimbus-registers, unlike riskCount, which real risk
  // registers do maintain incrementally) so the column always read 0 regardless of how many
  // documents actually existed. Computed here instead, from the same tenant-wide document list
  // DocumentsList.tsx's own "All Documents" view already fetches - no backend change needed.
  const { data: allDocuments = [] } = useQuery({
    queryKey: ['documents', '__all__'],
    queryFn: documentApi.listAll,
  });
  const countByRegisterId = useMemo(() => {
    const map = new Map<string, number>();
    allDocuments.forEach((d: any) => {
      if (d.entityType !== 'register' || !d.entityId) return;
      map.set(d.entityId, (map.get(d.entityId) || 0) + 1);
    });
    return map;
  }, [allDocuments]);

  // Convert templates to dropdown options (only show active templates)
  const templates = templatesData
    ?.filter((t: Template) => t.isActive)
    .map((t: Template) => ({
      value: t.templateName,
      label: t.displayName,
    })) || [];

  // Determine default template (first active template, or fallback)
  const defaultTemplate = templates.length > 0 ? templates[0].value : 'comprehensive';

  return (
    <RegisterManagementPage
      title="Document Registers"
      iconClass="ri-folder-line"
      queryKey="documentRegistersAdmin"
      api={documentRegistersApi}
      includeDepartments={false}
      viewButtonLabel="View Documents"
      rowLinks={[
        {
          label: 'Policy Templates',
          iconClass: 'ri-file-list-3-line',
          to: (r) => `/policy-templates?registerId=${encodeURIComponent(r.registerId)}`,
        },
      ]}
      templates={templates}
      defaultTemplate={defaultTemplate}
      listPath="/documents"
      countField="documentCount"
      countLabel="Documents"
      computeCount={(r) => countByRegisterId.get(r.registerId) || 0}
    />
  );
};
